import { useEffect, useMemo, useState } from 'react';
import { Gift, Loader2, Pencil } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { useGetUserBenefitsBalance } from '@/services';
import { EditUserBenefitUsageModal } from './EditUserBenefitUsageModal';

const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
	ACTIVE: 'Ativa',
	PENDING: 'Pendente',
	CANCELLED: 'Cancelada',
	EXPIRED: 'Expirada',
	SUSPENDED: 'Suspensa',
};

const SUBSCRIPTION_FILTER_DEFAULT = '__active_default__';

type UserClubSubscription = NonNullable<
	Users.User['user_club_subscription']
>[number];

interface UserClubBenefitsBalanceProps {
	userId: string;
	subscriptions: UserClubSubscription[];
	clubById: Record<string, Clubs.Club | undefined>;
}

function getBenefitDisplayName(benefit: Clubs.UserBenefitBalanceItem): string {
	const clubBenefit = benefit.club_benefit;
	const substitute = clubBenefit?.service_substitute_title?.trim();
	if (substitute) return substitute;
	const serviceName = clubBenefit?.service?.service_name?.trim();
	if (serviceName) return serviceName;
	return `Benefício ${benefit.club_benefit_id.slice(0, 8)}…`;
}

function formatSubscriptionOptionLabel(
	sub: UserClubSubscription,
	clubById: Record<string, Clubs.Club | undefined>,
): string {
	const clubName = sub.club?.name ?? clubById[sub.club_id]?.name ?? 'Clube';
	const statusLabel = SUBSCRIPTION_STATUS_LABEL[sub.status] ?? sub.status;
	return `${clubName} · ${statusLabel} · ${sub.id.slice(0, 8)}…`;
}

function formatBenefitRatio(value: number, defaultAmount: number): string {
	return `${value}/${defaultAmount}`;
}

const UserClubBenefitsBalance = ({
	userId,
	subscriptions,
	clubById,
}: UserClubBenefitsBalanceProps) => {
	const [subscriptionFilter, setSubscriptionFilter] = useState(
		SUBSCRIPTION_FILTER_DEFAULT,
	);
	const [editingRow, setEditingRow] =
		useState<Clubs.UserBenefitBalanceItem | null>(null);

	const subscriptionIdParam =
		subscriptionFilter === SUBSCRIPTION_FILTER_DEFAULT
			? undefined
			: subscriptionFilter;

	const { data, isLoading, isError, isFetching } = useGetUserBenefitsBalance(
		userId,
		subscriptionIdParam
			? { subscriptionId: subscriptionIdParam }
			: undefined,
	);

	useEffect(() => {
		setSubscriptionFilter(SUBSCRIPTION_FILTER_DEFAULT);
		setEditingRow(null);
	}, [userId]);

	const resolvedSubscriptionLabel = useMemo(() => {
		if (!data?.subscription_id) return null;
		const sub = subscriptions.find((s) => s.id === data.subscription_id);
		if (!sub) {
			return `Assinatura ${data.subscription_id.slice(0, 8)}…`;
		}
		return formatSubscriptionOptionLabel(sub, clubById);
	}, [data?.subscription_id, subscriptions, clubById]);

	const canEditUsage = data?.subscription_status === 'ACTIVE';

	return (
		<>
			<div className="space-y-4 rounded-2xl border border-zinc-100 bg-zinc-50/40 p-4 md:p-5">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div className="flex items-start gap-2">
						<div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-purple-100 bg-purple-50 text-purple-700">
							<Gift className="size-4" aria-hidden />
						</div>
						<div>
							<h3 className="text-sm font-bold text-zinc-900">
								Saldo de benefícios
							</h3>
							<p className="text-xs text-zinc-500">
								Uso e saldo restante por benefício da
								assinatura.
							</p>
						</div>
					</div>

					{subscriptions.length > 0 ? (
						<div className="w-full min-w-0 sm:max-w-md">
							<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">
								Assinatura consultada
							</span>
							<Select
								value={subscriptionFilter}
								onValueChange={setSubscriptionFilter}
							>
								<SelectTrigger className="h-10 w-full border-zinc-200 bg-white text-left text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem
										value={SUBSCRIPTION_FILTER_DEFAULT}
									>
										Assinatura ativa (padrão)
									</SelectItem>
									{subscriptions.map((sub) => (
										<SelectItem key={sub.id} value={sub.id}>
											{formatSubscriptionOptionLabel(
												sub,
												clubById,
											)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : null}
				</div>

				{isLoading || isFetching ? (
					<div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-10 text-sm text-zinc-500">
						<Loader2 className="size-4 animate-spin" aria-hidden />
						Carregando saldo…
					</div>
				) : isError ? (
					<p className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-6 text-center text-sm text-rose-800">
						Não foi possível carregar o saldo de benefícios.
					</p>
				) : !data?.subscription_id ? (
					<p className="rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
						Nenhuma assinatura ativa para consultar saldo.
					</p>
				) : data.benefits.length === 0 ? (
					<p className="rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
						Assinatura sem benefícios vinculados.
						{resolvedSubscriptionLabel ? (
							<span className="mt-1 block text-xs text-zinc-400">
								{resolvedSubscriptionLabel}
							</span>
						) : null}
					</p>
				) : (
					<div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
						{resolvedSubscriptionLabel ? (
							<p className="border-b border-zinc-100 px-4 py-2 text-[11px] text-zinc-500">
								Consultando:{' '}
								<span className="font-medium text-zinc-700">
									{resolvedSubscriptionLabel}
								</span>
								{data.subscription_status ? (
									<span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-600">
										{SUBSCRIPTION_STATUS_LABEL[
											data.subscription_status
										] ?? data.subscription_status}
									</span>
								) : null}
							</p>
						) : null}
						{!canEditUsage ? (
							<p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-[11px] text-amber-900">
								Edição de uso disponível apenas com assinatura{' '}
								<strong>ativa</strong>.
							</p>
						) : null}
						<table className="min-w-full text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-100 bg-zinc-50/80 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
									<th className="px-4 py-3">Serviço</th>
									<th className="px-4 py-3 text-center">
										Uso / valor padrão
									</th>
									<th className="w-14 px-4 py-3 text-center">
										<span className="sr-only">Ações</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{data.benefits.map((row) => (
									<tr
										key={row.id}
										className="border-b border-zinc-50 last:border-b-0"
									>
										<td className="px-4 py-3 font-medium text-zinc-900">
											{getBenefitDisplayName(row)}
										</td>
										<td className="px-4 py-3 text-center tabular-nums text-zinc-700">
											{formatBenefitRatio(
												row.used_count,
												row.default_amount,
											)}
										</td>
										<td className="px-4 py-3 text-center">
											{canEditUsage ? (
												<button
													type="button"
													onClick={() =>
														setEditingRow(row)
													}
													className="inline-flex size-9 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-800"
													aria-label={`Alterar uso de ${getBenefitDisplayName(
														row,
													)}`}
													title="Alterar uso"
												>
													<Pencil className="size-3.5 stroke-[2.5]" />
												</button>
											) : (
												<span className="text-zinc-300">
													—
												</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{editingRow ? (
				<EditUserBenefitUsageModal
					open
					onClose={() => setEditingRow(null)}
					userId={userId}
					row={editingRow}
					benefitName={getBenefitDisplayName(editingRow)}
				/>
			) : null}
		</>
	);
};

export { UserClubBenefitsBalance };
